import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  Inject,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { EncrDecrService } from '@mysrc/app/Providers/EncrDecrService';
import { BaseHttpReqRespService } from '@mysrc/app/modules/fwcore/services';
import { IsgGridComponent } from '@mysrc/app/shared/components/isg-grid';
import { IsgGridConfig } from '@mysrc/app/shared/models/grid-config.model';
import { NgbModal, NgbModalRef, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';

import { CommonService } from '../../../question/services/commonservice';
import { SurveyServiceLineService } from '../../services/survey-service-line.service';
import {
  ServiceLine,
  ManageServiceLineViewModel,
  Link,
  LinkDetails,
  ClientAdmin
} from '../../models/service-line.model';

@Component({
  selector: 'serviceline',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgbModule, AgGridAngular, IsgGridComponent],
  templateUrl: './page-survey-service-line.component.html',
  styleUrls: ['./page-survey-service-line.component.css'],
  providers: [CommonService, SurveyServiceLineService],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class PageSurveyServiceLineComponent extends BaseHttpReqRespService implements OnInit {

  // ── ViewChild modal refs (NgbModal pattern — same as user) ─
  @ViewChild('serviceLineModal') serviceLineModal!: TemplateRef<any>;
  @ViewChild('deleteServiceLineModal') deleteServiceLineModal!: TemplateRef<any>;
  @ViewChild('externalLinkModal') externalLinkModal!: TemplateRef<any>;

  modalRef: NgbModalRef | undefined;

  // ── Forms ──────────────────────────────────────────────────
  serviceLineForm!: FormGroup;
  externalLinkForm!: FormGroup;

  // ── Grid data ──────────────────────────────────────────────
  serviceLineList: ServiceLine[] = [];
  serviceLineServiceProviderMapList: ManageServiceLineViewModel[] = [];
  serviceLineSurveyMapList: ManageServiceLineViewModel[] = [];
  linkList: Link[] = [];
  clientAdminList: ClientAdmin[] = [];

  // ── State ──────────────────────────────────────────────────
  currentServiceLineId: number = 0;
  tmpWorkspaceId: string = '';
  successOrFailureMessage: string = '';
  private gridApi: any;

  // ── ISG Grid config (same pattern as user) ─────────────────
  gridConfig: Partial<IsgGridConfig> = {
    height: '500px',
    pagination: true,
    pageSize: 10,
    autoSizeColumns: true
  };

  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true
  };

  // ── Column definitions ─────────────────────────────────────
  columnDefs: ColDef[] = [
    { field: 'serviceLineId', hide: true },
    {
      headerName: 'Name',
      field: 'serviceLineName',
      resizable: true,
      cellRenderer: (params: any) => this.linkRenderer(params)
    },
    {
      headerName: 'Manage Survey and Provider Company',
      resizable: true,
      cellRenderer: (params: any) => this.manageServiceLineCellRenderer(params)
    },
    { headerName: 'Created By', field: 'createdBy', resizable: true },
    { headerName: 'Modified By', field: 'modifiedBy', resizable: true },
    { headerName: 'Created Date', field: 'createdDate', resizable: true },
    { headerName: 'Modified Date', field: 'modifiedDate', resizable: true },
    {
      headerName: 'Delete',
      resizable: true,
      cellRenderer: (params: any) => this.deleteImageRenderer(params)
    }
  ];

  constructor(
    @Inject(OKTA_AUTH) private oauth: OktaAuth,
    protected http: HttpClient,
    private EncrDecr: EncrDecrService,
    private formBuilder: FormBuilder,
    private renderer: Renderer2,
    private router: Router,
    private modalService: NgbModal,
    private surveyServiceLineService: SurveyServiceLineService,
    @Inject('BASE_URL') baseUrl: string
  ) {
    super(http, oauth);
  }

  ngOnInit(): void {
    this.tmpWorkspaceId = this.EncrDecr.get(localStorage.getItem('workspaceId') ?? '');
    this.initForms();
    this.loadServiceLineList();
  }

  // ── Form initialisation ────────────────────────────────────
  private initForms(): void {
    this.serviceLineForm = this.formBuilder.group({
      serviceLineId: new FormControl(0),
      serviceLineName: new FormControl('', [
        Validators.required,
        this.noWhitespaceValidator,
        this.serviceLineExistsValidator()
      ]),
      serviceLineDescription: new FormControl(''),
      isActive: new FormControl(false),
      showAnalysisReport: new FormControl(true),
      showOverviewReport: new FormControl(true),
      showGroupingAndCommunicationIcon: new FormControl(false)
    });

    this.externalLinkForm = this.formBuilder.group({
      linkId: new FormControl(null, Validators.required),
      serviceLineId: new FormControl(null, Validators.required),
      companyId: new FormControl(null, Validators.required),
      surveyId: new FormControl(null, Validators.required),
      clientAdminId: new FormControl(null, Validators.required)
    });
  }

  // ── Load service line list ─────────────────────────────────
  loadServiceLineList(): void {
    this.surveyServiceLineService.getServiceLineList(this.tmpWorkspaceId).subscribe({
      next: (result: ServiceLine[]) => this.serviceLineList = result,
      error: (err: unknown) => console.error(err)
    });
  }

  // ── Grid ready ─────────────────────────────────────────────
  onGridReady(params: any): void {
    this.gridApi = params.api;
    params.api.sizeColumnsToFit();
  }

  // ── Open modals (NgbModal pattern — same as user) ──────────
  openCreateServiceLineModal(): void {
    this.currentServiceLineId = 0;
    this.serviceLineForm.reset({
      serviceLineId: 0,
      isActive: false,
      showAnalysisReport: true,
      showOverviewReport: true,
      showGroupingAndCommunicationIcon: false
    });
    this.modalRef = this.modalService.open(this.serviceLineModal, {
      backdrop: 'static',
      keyboard: false
    });
  }

  openDeleteServiceLineModal(): void {
    this.modalRef = this.modalService.open(this.deleteServiceLineModal, {
      backdrop: 'static',
      keyboard: false
    });
  }

  openExternalLinkModal(): void {
    this.getLinkList();
    this.modalRef = this.modalService.open(this.externalLinkModal, {
      size: 'md',
      backdrop: 'static'
    });
  }

  // ── Close / reset ──────────────────────────────────────────
  onClose(): void {
    this.initForms();
  }

  closeExternalLinkForm(): void {
    this.externalLinkForm.reset();
  }

  // ── Cell renderers (Renderer2 — same pattern as user) ──────
  linkRenderer(params: any): HTMLElement {
    const element = this.renderer.createElement('a');
    const text = this.renderer.createText(params.value);
    this.renderer.appendChild(element, text);
    this.renderer.setAttribute(element, 'href', 'javascript:void(0)');
    this.renderer.listen(element, 'click', () => {
      this.currentServiceLineId = params.data.serviceLineId;
      this.surveyServiceLineService.editServiceLine(params.data.serviceLineId).subscribe({
        next: (response: ServiceLine) => {
          this.serviceLineForm.patchValue({
            serviceLineId: response.serviceLineId,
            serviceLineName: response.serviceLineName,
            serviceLineDescription: response.serviceLineDescription,
            isActive: response.isActive,
            showAnalysisReport: response.showAnalysisReport,
            showOverviewReport: response.showOverviewReport,
            showGroupingAndCommunicationIcon: response.showGroupingAndCommunicationIcon
          });
          this.modalRef = this.modalService.open(this.serviceLineModal, {
            backdrop: 'static',
            keyboard: false
          });
        },
        error: (err: unknown) => console.error(err)
      });
    });
    return element;
  }

  deleteImageRenderer(params: any): HTMLElement {
    const element = this.renderer.createElement('a');
    const icon = this.renderer.createElement('i');
    icon.setAttribute('class', 'fa fa-trash anchor-cursor');
    this.renderer.appendChild(element, icon);
    this.renderer.listen(element, 'click', () => {
      this.currentServiceLineId = params.data.serviceLineId;
      this.openDeleteServiceLineModal();
    });
    return element;
  }

  manageServiceLineCellRenderer(params: any): HTMLElement {
    const element = this.renderer.createElement('a');
    const icon = this.renderer.createElement('i');
    icon.setAttribute('class', 'fa fa-list anchor-cursor');
    this.renderer.appendChild(element, icon);
    this.renderer.listen(element, 'click', () => {
      this.router.navigate(['manageserviceline', {
        serviceLineId: params.data.serviceLineId,
        serviceLineName: params.data.serviceLineName
      }]);
    });
    return element;
  }

  // ── Save service line ──────────────────────────────────────
  saveServiceLine(): void {
    if (this.serviceLineForm.invalid) return;
    this.surveyServiceLineService.saveServiceLine(this.serviceLineForm.value).subscribe({
      next: (result: ServiceLine[]) => {
        this.serviceLineList = result;
        this.currentServiceLineId = 0;
        this.successOrFailureMessage = 'Service Line saved successfully.';
        this.messageTimeout();
        this.onClose();
      },
      error: (err: unknown) => console.error(err)
    });
  }

  // ── Delete service line ────────────────────────────────────
  deleteServiceLine(): void {
    this.surveyServiceLineService.deleteServiceLine(this.currentServiceLineId).subscribe({
      next: (result: any) => {
        this.serviceLineList = result.listOfServiceLines ?? [];
        this.successOrFailureMessage = result.messageInfo ?? '';
        this.currentServiceLineId = 0;
        this.messageTimeout();
      },
      error: (err: unknown) => console.error(err)
    });
  }

  // ── External link methods ──────────────────────────────────
  getLinkList(): void {
    this.surveyServiceLineService.getLinkList().subscribe({
      next: (result: Link[]) => this.linkList = result,
      error: (err: unknown) => console.error(err)
    });
  }

  getMappedLink(): void {
    const linkId = this.externalLinkForm.get('linkId')?.value;
    if (!linkId) return;
    this.surveyServiceLineService.getMappedLink(linkId).subscribe({
      next: (result: LinkDetails) => {
        this.externalLinkForm.patchValue({
          serviceLineId: result.serviceLineId,
          companyId: result.companyId,
          surveyId: result.surveyId,
          linkId: result.linkId,
          clientAdminId: result.clientAdminId
        });
        this.getMappedServiceProvider();
        this.getMappedSurvey();
        this.getClientAdminList();
      },
      error: (err: unknown) => console.error(err)
    });
  }

  getMappedServiceProvider(): void {
    const serviceLineId = this.externalLinkForm.get('serviceLineId')?.value;
    if (!serviceLineId) return;
    this.surveyServiceLineService.getMappedServiceProviderList(serviceLineId).subscribe({
      next: (result: ManageServiceLineViewModel[]) => {
        this.serviceLineServiceProviderMapList = result;
        this.getMappedSurvey();
      },
      error: (err: unknown) => console.error(err)
    });
  }

  getMappedSurvey(): void {
    const serviceLineId = this.externalLinkForm.get('serviceLineId')?.value;
    const companyId = this.EncrDecr.get(localStorage.getItem('companyId') ?? '');
    if (!serviceLineId) return;
    this.surveyServiceLineService.getMappedSurveyList(serviceLineId, companyId).subscribe({
      next: (result: ManageServiceLineViewModel[]) => this.serviceLineSurveyMapList = result,
      error: (err: unknown) => console.error(err)
    });
  }

  getClientAdminList(): void {
    const companyId = this.externalLinkForm.get('companyId')?.value;
    if (!companyId) return;
    this.surveyServiceLineService.getClientAdminList(companyId).subscribe({
      next: (result: ClientAdmin[]) => this.clientAdminList = result,
      error: (err: unknown) => console.error(err)
    });
  }

  saveLinkMapping(): void {
    if (this.externalLinkForm.invalid) return;
    this.surveyServiceLineService.saveLink(this.externalLinkForm.value).subscribe({
      next: () => {
        this.externalLinkForm.reset();
        this.successOrFailureMessage = 'Link updated successfully.';
        this.messageTimeout();
      },
      error: (err: unknown) => console.error(err)
    });
  }

  // ── Validators ─────────────────────────────────────────────
  noWhitespaceValidator(control: FormControl): { [key: string]: boolean } | null {
    const isWhitespace = (control.value ?? '').trim().length === 0;
    return isWhitespace ? { whitespace: true } : null;
  }

  serviceLineExistsValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const currentId = this.serviceLineForm?.get('serviceLineId')?.value ?? 0;
      const exists = this.serviceLineList.some(
        sl => sl.serviceLineName?.trim().toLowerCase() === control.value?.trim().toLowerCase()
          && sl.serviceLineId !== currentId
      );
      return exists ? { servicelineExists: true } : null;
    };
  }

  // ── Message helper ─────────────────────────────────────────
  messageTimeout(): void {
    window.setTimeout(() => this.successOrFailureMessage = '', 3200);
  }
}
